import { FlexRow } from '@/components/common/Flex';
import InputField from '@/components/common/InputField';
import TransactionScheduleInput from '@/components/common/TransactionScheduleInput';
import { Text } from '@/components/common/Typography';
import { configByGoalType } from '@/components/goals/config';
import { useGoalOnboardingContext } from '@/components/goals/detail/onboarding/GoalOnboardingContext';
import {
    getGoalTransactionScheduleInputSliderConfig,
    useInitialDepositPlanSliderConfig,
} from '@/components/goals/helpers';
import { Circle, Grid, GridProps } from '@chakra-ui/react';
import {
    getAmountRecurrenceStr,
    getPartnerGoalAccount,
} from '@common/react/goals';
import { getPartnerName, useCurrentUser } from '@common/react/user';
import styleguide from '@common/styleguide';
import { TransactionSchedulePlan } from '@common/types';

interface InputsContainerProps extends GridProps {
    initialDepositPlan: number;
    onChangeInitialDepositPlan: (initialDepositPlan: number) => void;
    transactionSchedulePlan: TransactionSchedulePlan;
    onChangeTransactionSchedulePlan: (
        transactionSchedulePlan: TransactionSchedulePlan
    ) => void;
    partnerTransactionSchedulePlan?: TransactionSchedulePlan;
}

const InputsContainer = ({
    initialDepositPlan,
    onChangeInitialDepositPlan,
    transactionSchedulePlan,
    onChangeTransactionSchedulePlan,
    partnerTransactionSchedulePlan,
    ...props
}: InputsContainerProps) => {
    const { goal } = useGoalOnboardingContext();
    const { partnerInfo } = useCurrentUser();
    const isSharedGoal = goal.shareStatus === 'SHARED';
    const partnerGoalAccount = getPartnerGoalAccount(goal);
    const partnerName = getPartnerName(partnerInfo);
    const initialDepositPlanSliderConfig = useInitialDepositPlanSliderConfig({
        initialDepositPlan,
        targetAmount: goal.targetAmount,
    });
    const transactionSchedulePlanSliderConfig =
        getGoalTransactionScheduleInputSliderConfig({
            targetAmount: goal.targetAmount,
            startAmount: initialDepositPlan,
        });
    const goalConfig = configByGoalType[goal.type];
    const fundingType =
        goalConfig.group === 'HIGH_YIELD_CASH_ONLY'
            ? 'contribution'
            : 'investment';

    return (
        <Grid
            bg="gray.50"
            px={{ base: 8, '2xl': 12 }}
            py={{ base: 6, '2xl': 8 }}
            rounded="md"
            templateColumns="130px 10px 1.5fr 1fr"
            gap={10}
            {...props}
        >
            <InputField
                name="initialDepositPlan"
                label={`My initial ${fundingType}`}
                labelProps={{ whiteSpace: 'nowrap' }}
                type="currency"
                value={initialDepositPlan}
                onChange={onChangeInitialDepositPlan}
                inputProps={{
                    variant: 'flushed',
                    size: { base: 'lg', '2xl': 'xl' },
                }}
                inputOptions={{ min: 0 }}
                slider={initialDepositPlanSliderConfig}
            />
            <Text mt={8}>+</Text>
            <TransactionScheduleInput
                datePickerLabel={
                    <FlexRow alignItems="center" gap={2} whiteSpace="nowrap">
                        {isSharedGoal ? (
                            <>
                                <Circle
                                    size={1.5}
                                    bg={styleguide.colors.blueBright}
                                />
                                {`Repeating ${fundingType} starting`}
                            </>
                        ) : (
                            `Repeating ${fundingType} starting`
                        )}
                    </FlexRow>
                }
                transactionSchedule={transactionSchedulePlan}
                onChangeTransactionSchedule={onChangeTransactionSchedulePlan}
                slider={transactionSchedulePlanSliderConfig}
                showDatePicker
                datePickerValue={transactionSchedulePlan.startDate}
                inputProps={{
                    variant: 'flushed',
                    size: { base: 'lg', '2xl': 'xl' },
                }}
            />
            {partnerTransactionSchedulePlan && (
                <InputField
                    name="partnerTransactionSchedule"
                    label={
                        <FlexRow
                            alignItems="center"
                            gap={2}
                            whiteSpace="nowrap"
                        >
                            <Circle
                                size={1.5}
                                bg={styleguide.colors.orangeBright}
                            />
                            {partnerGoalAccount
                                ? `${partnerName} is already ${
                                      goalConfig.group ===
                                      'HIGH_YIELD_CASH_ONLY'
                                          ? 'contributing'
                                          : 'investing'
                                  }`
                                : `${partnerName}'s suggested ${fundingType}`}
                        </FlexRow>
                    }
                    type="text"
                    value={getAmountRecurrenceStr(
                        partnerTransactionSchedulePlan
                    )}
                    onChange={() => undefined}
                    inputProps={{
                        variant: 'flushed',
                        size: { base: 'lg', '2xl': 'xl' },
                    }}
                    isReadOnly
                />
            )}
        </Grid>
    );
};

export default InputsContainer;
